<template>
  <div class="vehicle-edit" v-if="vehicle">

    <div class="d-flex justify-end">
      <audit-trail-button entity-type="Vehicle" :entity-id="$route.params.id" />
    </div>


    <v-alert v-if="vehicle.archived" type="warning" text dense class="mb-6">
      {{ $t('vehicle.archived-since', { date: formatDate(vehicle.archivedAt, $t('format_datetime')) }) }}
    </v-alert>

    <v-form ref="form" @submit.prevent="save">

      <headline>
        {{ $t('information') }}
      </headline>

      <v-row>
        <v-col cols="12" md="6">
          <form-field
              :label="$t('vehicle.name')"
              v-model="form.name"
              :rules="[commonRules.required]"
          />
        </v-col>
        <v-col cols="12" md="6">
          <form-field
              :label="$t('vehicle.registration-number')"
              v-model="form.registrationNumber"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <form-field
              :label="$t('vehicle.vin')"
              v-model="form.vin"
              :disabled="vinLocked"
              :hint="vinLocked ? $t('vehicle.vin-locked-connected') : null"
              :hide-details="!vinLocked"
          />
        </v-col>
        <v-col cols="12" md="6">
          <form-field
              :label="$t('vehicle.country')"
              type="available-select"
              input-type="country"
              v-model="form.country"
          />
        </v-col>
      </v-row>

      <headline class="mt-6">
        {{ $t('vehicle.brand') }} / {{ $t('vehicle.model') }} / {{ $t('vehicle.version') }}
      </headline>

      <vehicle-brand-model-version-selector
          v-model="form.versionId"
          :initial-version="vehicle ? vehicle.version : null"
      />

      <v-row class="mt-6">
        <v-col class="d-flex justify-center">
          <v-btn small class="mr-5" @click="cancel">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn small color="primary" type="submit">
            {{ $t('save') }}
          </v-btn>
        </v-col>
      </v-row>

    </v-form>

    <v-divider class="my-6" />

    <v-row>
      <v-col class="d-flex">
        <v-btn v-if="!vehicle.archived" small color="warning" @click="showArchiveDialog = true">
          <v-icon left small>mdi-archive</v-icon>
          {{ $t('vehicle.archive') }}
        </v-btn>

        <v-btn v-else small color="success" @click="unarchiveVehicle">
          <v-icon left small>mdi-archive-arrow-up</v-icon>
          {{ $t('vehicle.unarchive') }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Archive Dialog -->
    <v-dialog v-model="showArchiveDialog" max-width="500">
      <v-card>
        <v-card-title>{{ $t('vehicle.archive') }}</v-card-title>
        <v-card-text>
          <p>{{ $t('vehicle.confirm-archive') }}</p>
          <v-text-field
              :label="$t('vehicle.reason') + ' (' + $t('optional') + ')'"
              v-model="archiveReason"
              outlined
              hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small @click="showArchiveDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="warning" @click="archiveVehicle">{{ $t('vehicle.archive') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";
import VehicleRepository from "@repository/VehicleRepository";
import VehicleBrandModelVersionSelector from "@blocks/Select/VehicleBrandModelVersionSelector";
import RulesMixin from "@mixins/RulesMixin";

export default {
  components: {AuditTrailButton,  VehicleBrandModelVersionSelector },
  mixins: [RulesMixin],

  data() {
    return {
      form: {
        name: null,
        registrationNumber: null,
        vin: null,
        country: null,
        versionId: null,
      },
      showArchiveDialog: false,
      archiveReason: null,
      // The VIN is the vehicle's identity at the telematics provider: it is locked while a connection is
      // active (change it via terminate → edit → reconnect, mirroring the backend guard).
      vinLocked: false,
    }
  },

  mounted() {
    if (!this.currentUser.permissions.includes('VEHICLE_WRITE')) {
      this.$router.replace({ name: 'dashboard' })
      return
    }
    this.initForm()
  },

  computed: {
    vehicle() {
      return this.$parent.vehicle
    },
  },

  watch: {
    vehicle() {
      this.initForm()
    },
  },

  methods: {
    initForm() {
      if (this.vehicle) {
        this.form.name = this.vehicle.name
        this.form.registrationNumber = this.vehicle.registrationNumber
        this.form.vin = this.vehicle.vin
        this.form.country = this.vehicle.country

        if (this.vehicle.version) {
          this.form.versionId = this.vehicle.version.id
        }

        // Lock the VIN when the vehicle is connected to a telematics provider (matches the backend guard).
        VehicleRepository.telematics(this.vehicle.id)
          .then(res => { this.vinLocked = res.connected === true })
          .catch(() => { this.vinLocked = false })
      }
    },

    cancel() {
      this.$router.push({ name: 'vehicle.detail', params: { id: this.vehicle.id } })
    },

    save() {
      if (this.$refs.form.validate()) {
        this.showLoading(true)
        VehicleRepository.update(this.vehicle.id, this.form).then(() => {
          this.showLoading(false)
          this.$parent.loadData().then(() => {
            this.$router.push({ name: 'vehicle.detail', params: { id: this.vehicle.id } })
          })
        }).catch(err => {
          this.showLoading(false)
          this.notifyError(err)
        })
      }
    },

    archiveVehicle() {
      const data = {}
      if (this.archiveReason) data.reason = this.archiveReason

      this.showLoading(true)
      VehicleRepository.archive(this.vehicle.id, data).then(() => {
        this.showLoading(false)
        this.showArchiveDialog = false
        this.archiveReason = null
        this.$parent.loadData()
      }).catch(err => {
        this.showLoading(false)
        this.notifyError(err)
      })
    },

    unarchiveVehicle() {
      this.$dialog.confirm({ text: this.$t('vehicle.confirm-unarchive') }).then(result => {
        if (result) {
          this.showLoading(true)
          VehicleRepository.unarchive(this.vehicle.id).then(() => {
            this.showLoading(false)
            this.$parent.loadData()
          }).catch(err => {
            this.showLoading(false)
            this.notifyError(err)
          })
        }
      })
    },
  },
}
</script>

<style lang="scss">
</style>
